// ============================================================================
// New Account SOP — landing page. Kanban / List view toggle over
// new_account_sop with shared filters and a click-through detail drawer.
//
// Layout:
//   * Top: header (title + counts + view toggle + refresh)
//   * Tab bar: Board · Needs Info
//   * Filter bar (search, status, has-start, stage chips, market chips)
//   * Either Kanban (default) or List view
//   * Detail drawer slides in over content when a card/row is clicked
//
// Cowork 02 (2026-06-28):
//   * KEY-tier open-requirement counts (v_account_open_requirements) are
//     fetched and passed as `hasOpenKeyReqs` into the lifecycle calls, so a
//     started-but-incomplete card STAYS on the board until KEY clears.
//   * Start-date chips ("Starts today" / "Overdue start — confirm started?")
//     on the board cards via countdownLabel().
//   * A "Needs Info" tab (also reachable at /new-accounts/needs-info) renders
//     the persistent Needs-Info surface.
//
// Mobile: the toggle defaults to Kanban (which collapses gracefully to
// stacked stage sections on narrow viewports). Filter bar wraps; the table
// in List view scrolls horizontally inside its container.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, FileClock, Plus, RefreshCw } from 'lucide-react';
import { Button, ErrorState, PageHeader, Skeleton, useToast } from '../../shared/components';
import { ExplainerButton } from '../../shared/explainer';
import { supabase } from '../../shared/supabase';
import type { NewAccountSop, NewAccountSopActiveStage } from '../../shared/types';
import { errorMessage } from '../../shared/errors';
import { blockedStageMoveReason } from './intake-answers';
import { useNewAccountSops } from './hooks/use-sop-data';
import { useNeedsInfo } from './hooks/use-needs-info';
import { isOnBoard, isArchived, isBehind, countdownLabel } from './lib/lifecycle';
import { cardDisplayName } from './card-name';
import { ResolutionDialog, type ResolutionTarget } from './resolution-dialog';
import NeedsInfoBoard from './needs-info-board';
import SopFilters, {
  DEFAULT_FILTERS,
  NO_MARKET_TOKEN,
  type SopFilterState,
} from './sop-filters';
import SopKanban from './sop-kanban';
import SopList from './sop-list';
import SopDetail from './sop-detail';
import SopCreateModal from './sop-create-modal';
import { localToday } from '../../shared/format';

type ViewMode = 'kanban' | 'list';
type Tab = 'board' | 'needs-info' | 'archived';

export default function NewAccountSopPage({ initialTab = 'board' }: { initialTab?: Tab }) {
  const {
    data,
    isLoading,
    error,
    refetch,
    patchRow,
    deleteRow,
    refusalNotice,
    clearRefusalNotice,
  } = useNewAccountSops();
  // Cowork 02: KEY-tier open-requirement counts keep started-but-incomplete
  // cards on the board. Cheap (two small view reads); shared with the tab.
  const { keyCountBySop } = useNeedsInfo();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [view, setView] = useState<ViewMode>('kanban');
  const [filters, setFilters] = useState<SopFilterState>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<NewAccountSop | null>(null);
  // P0 (2026-05-28): page-level create button. The same modal is also wired
  // into NavShell so AMs can create from any screen.
  const [createOpen, setCreateOpen] = useState(false);
  // Resume-a-draft (#4). Set to a draft row → opens the create modal hydrated.
  const [resumeDraft, setResumeDraft] = useState<NewAccountSop | null>(null);
  // D4 resolution popup target (Behind card → reconcile start date).
  const [resolution, setResolution] = useState<ResolutionTarget | null>(null);
  const [behindOpen, setBehindOpen] = useState(true);
  const toast = useToast();

  // ⛔ A field the save door refused is NAMED, never swallowed. The rest of the
  // patch landed and the refused field has already been put back on screen, so
  // this says which one did not move and why, in the database's own words.
  // duration 0 so it stays until dismissed — a refusal the person scrolls past
  // is a refusal they will make again.
  useEffect(() => {
    if (!refusalNotice) return;
    toast.error('Some of that could not be changed from this card', {
      description: refusalNotice,
      duration: 0,
    });
    clearRefusalNotice();
  }, [refusalNotice, toast, clearRefusalNotice]);

  // TCSD-0082 — a card you can be SENT to belongs in the URL, not in useState.
  // My Work now links "Executive review sign-off needed — Davita Frisco" to
  // /new-accounts?card=SOP-0206, and that link has to open the drawer on the
  // card it names or the requirement still has no screen that clears it.
  // Landing with no parameter = the board, unchanged.
  const [searchParams, setSearchParams] = useSearchParams();
  const cardParam = searchParams.get('card');
  useEffect(() => {
    if (!cardParam) return;
    if (selected?.sop_id === cardParam) return;
    const hit = data.find((r) => r.sop_id === cardParam);
    if (hit) setSelected(hit);
  }, [cardParam, data, selected?.sop_id]);

  // Keep the URL and the drawer in step, so Back closes it and a copied link
  // reopens the same card. `replace` — a drawer is not a page in the history.
  const openCard = useCallback(
    (r: NewAccountSop | null) => {
      setSelected(r);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (r) next.set('card', r.sop_id);
          else next.delete('card');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Does this SOP have ≥1 open KEY requirement? Drives board no-drop.
  const hasOpenKey = useMemo(
    () => (sopId: string) => (keyCountBySop.get(sopId) ?? 0) > 0,
    [keyCountBySop],
  );

  // Drafts (is_draft=true) are excluded from the board/list/pipeline and shown
  // in their own resumable strip (#4). Everything else works off liveData.
  const liveData = useMemo(() => data.filter((r) => !r.is_draft), [data]);
  const drafts = useMemo(() => data.filter((r) => r.is_draft), [data]);

  // Distinct market_region values present (excluding null) — passed to the
  // filter bar so it only renders chips for markets that exist.
  const availableMarkets = useMemo(() => {
    const set = new Set<string>();
    for (const r of liveData) {
      if (r.market_region && r.market_region.trim().length > 0) {
        set.add(r.market_region);
      }
    }
    return Array.from(set).sort();
  }, [liveData]);

  // 2026-07-06 — provider alias map so cards are findable by alias ("Byron"
  // finds the DKK card via service_provider_id). Fetched once; soft-fails.
  const [providerAliases, setProviderAliases] = useState<Map<string, string[]>>(
    new Map(),
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: al, error: aliasErr } = await supabase
        .from('entity_aliases')
        .select('entity_id, alias')
        .eq('entity_type', 'service_provider');
      if (aliasErr || cancelled) return;
      const m = new Map<string, string[]>();
      for (const r of (al ?? []) as { entity_id: string; alias: string }[]) {
        const list = m.get(r.entity_id) ?? [];
        list.push(r.alias);
        m.set(r.entity_id, list);
      }
      setProviderAliases(m);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply all filters in one pass — small dataset (~74 rows), so a single
  // filter chain is cheap and easy to read.
  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return liveData.filter((r) => {
      // Status mode
      if (filters.statusMode === 'active' && r.status !== 'Active') return false;
      if (filters.statusMode === 'stalled' && r.status !== 'Stalled') return false;
      // Has start date
      if (filters.hasStartDate === 'yes' && !r.start_date) return false;
      if (filters.hasStartDate === 'no' && r.start_date) return false;
      // Market region — match any selected; the special NO_MARKET_TOKEN
      // selects rows where market_region IS NULL/empty.
      if (filters.markets.length > 0) {
        const m = r.market_region?.trim() ?? '';
        const wantsNoMarket = filters.markets.includes(NO_MARKET_TOKEN);
        const matchesNamed = m && filters.markets.includes(m);
        const matchesNoMarket = wantsNoMarket && m === '';
        if (!matchesNamed && !matchesNoMarket) return false;
      }
      // Stage — match any selected
      if (filters.stages.length > 0) {
        if (!filters.stages.includes(r.stage as never)) return false;
      }
      // Search — card title fields + provider aliases (2026-07-06)
      if (q.length > 0) {
        const hay = [r.account_name, r.facility_name, r.contractor_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const aliasHit =
          r.service_provider_id != null &&
          (providerAliases.get(r.service_provider_id) ?? []).some((a) =>
            a.toLowerCase().includes(q),
          );
        if (!hay.includes(q) && !aliasHit) return false;
      }
      return true;
    });
  }, [liveData, filters, providerAliases]);

  // NEW ACCOUNTS membership (Option D PR-4a + Cowork 02): the board shows only
  // in-flight cards that aren't behind; behind cards (planned start passed, not
  // confirmed started, has a facility) drop into the ⚠ Behind strip;
  // started/recent/cancelled cards leave the board — UNLESS they still have
  // open KEY info, in which case they remain (started_incomplete).
  const behindRows = useMemo(
    () => filtered.filter((r) => isBehind(r, hasOpenKey(r.sop_id))),
    [filtered, hasOpenKey],
  );
  const boardRows = useMemo(
    () =>
      filtered.filter((r) => {
        const k = hasOpenKey(r.sop_id);
        return isOnBoard(r, k) && !isBehind(r, k);
      }),
    [filtered, hasOpenKey],
  );

  // Archived lens (Task 3, 2026-07-14): the read-only complement of the board —
  // cleanly-started, cancelled, or Completed cards that dropped off but are
  // retained in the DB. Honors ONLY search + market (status/stage/has-start
  // chips are board concerns and would hide cancelled/completed cards here).
  const archivedRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return liveData.filter((r) => {
      if (!isArchived(r, hasOpenKey(r.sop_id))) return false;
      if (filters.markets.length > 0) {
        const m = r.market_region?.trim() ?? '';
        const wantsNoMarket = filters.markets.includes(NO_MARKET_TOKEN);
        const matchesNamed = m && filters.markets.includes(m);
        const matchesNoMarket = wantsNoMarket && m === '';
        if (!matchesNamed && !matchesNoMarket) return false;
      }
      if (q.length > 0) {
        const hay = [r.account_name, r.facility_name, r.contractor_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const aliasHit =
          r.service_provider_id != null &&
          (providerAliases.get(r.service_provider_id) ?? []).some((a) =>
            a.toLowerCase().includes(q),
          );
        if (!hay.includes(q) && !aliasHit) return false;
      }
      return true;
    });
  }, [liveData, filters.search, filters.markets, hasOpenKey, providerAliases]);

  // Cancel a card from the D4 popup (off the board, with a reason).
  const handleCancelCard = async (sopId: string, reason: string) => {
    await patchRow(sopId, {
      status: 'Cancelled',
      stage: 'Cancelled',
      cancelled_date: localToday(),
      cancelled_reason: reason,
    });
  };

  // When the drawer is open and a patch lands, surface the change in the
  // open drawer too (the underlying `data` updates via the optimistic
  // path, but `selected` is a stale snapshot of the row at click time).
  const handlePatch = async (
    sopId: string,
    patch: Parameters<typeof patchRow>[1],
  ) => {
    await patchRow(sopId, patch);
    setSelected((prev) =>
      prev && prev.sop_id === sopId ? { ...prev, ...patch } : prev,
    );
  };

  // AFB-0081 — the Kanban drag is the second stage write path (the detail
  // footer is the first, the checklist auto-promote the third), and until now
  // its ONLY failure surface was console.error: the card snapped back to its old
  // column and said nothing at all. The database now refuses a move that carries
  // an unanswered intake question, so a silent snap-back would read as a broken
  // board — which is how AFB-0079 happened, a save that worked and a UI that
  // never said so.
  //
  // Checked here BEFORE the write so the message can name the missing fields;
  // still reported if the write is refused anyway, because this check is the
  // courtesy and the trigger is the guarantee.
  const handleStageDrop = async (
    sopId: string,
    nextStage: NewAccountSopActiveStage,
  ) => {
    const row = liveData.find((r) => r.sop_id === sopId);
    const blocked = row ? blockedStageMoveReason(row, nextStage) : null;
    if (blocked) {
      toast.error('Not ready for the next stage', { description: blocked });
      return;
    }
    try {
      await handlePatch(sopId, { stage: nextStage });
    } catch (err) {
      toast.error("Couldn't move the card", {
        description: errorMessage(
          err,
          'The stage change was rolled back.',
          'new-accounts/kanban',
        ),
      });
      throw err; // keep the Kanban's own logging path
    }
  };

  // #6 — delete a card (owner-or-admin, reason-required) via the RPC, then
  // close the drawer. deleteRow optimistically drops it from the board.
  const handleDelete = async (sopId: string, reason: string) => {
    await deleteRow(sopId, reason);
    openCard(null);
    toast.success('Card deleted', { description: 'Removed from the board.' });
  };

  return (
    <div className="mx-auto max-w-7xl p-4 pb-12 sm:p-6">
      <PageHeader
        title="New Accounts"
        meta={
          isLoading
            ? 'Loading…'
            : `${boardRows.length} in flight · ${behindRows.length} behind · ${liveData.length} total`
        }
        actions={
          <>
            <ExplainerButton surfaceKey="board:new-account-sop" />
            {tab === 'board' && (
              <div className="flex items-center gap-0.5 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() => setView('kanban')}
                  className={`rounded px-3 py-1 ${view === 'kanban' ? 'bg-tcs-surface font-medium text-tcs-text' : 'text-tcs-text-secondary'}`}
                >
                  Kanban
                </button>
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className={`rounded px-3 py-1 ${view === 'list' ? 'bg-tcs-surface font-medium text-tcs-text' : 'text-tcs-text-secondary'}`}
                >
                  List
                </button>
              </div>
            )}

            <Button
              variant="secondary"
              size="sm"
              onClick={refetch}
              disabled={isLoading}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Refresh
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateOpen(true)}
              icon={<Plus className="h-3.5 w-3.5" />}
            >
              New account
            </Button>
          </>
        }
      />

      {/* ---------- Tabs (Board · Needs Info) ---------- */}
      <div className="mt-4 flex items-center gap-1 border-b border-tcs-border">
        {(['board', 'needs-info', 'archived'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t
                ? 'border-tcs-accent text-tcs-text'
                : 'border-transparent text-tcs-text-secondary hover:text-tcs-text'
            }`}
          >
            {t === 'board' ? 'Board' : t === 'needs-info' ? 'Needs Info' : 'Archived'}
          </button>
        ))}
      </div>

      {tab === 'needs-info' ? (
        <div className="mt-4">
          <NeedsInfoBoard />
        </div>
      ) : tab === 'archived' ? (
        <div className="mt-4 space-y-4">
          {error && <ErrorState body={error} onRetry={refetch} />}
          <SopFilters
            filters={filters}
            onChange={setFilters}
            availableMarkets={availableMarkets}
            visibleCount={archivedRows.length}
            totalCount={liveData.length}
          />
          <p className="text-xs text-tcs-text-secondary">
            Read-only archive of accounts that have left the board — cleanly started, cancelled, or
            completed. {archivedRows.length} shown. Search by account, facility, or contractor;
            filter by market above. (Status / stage / start-date chips don’t apply here.)
          </p>
          {archivedRows.length > 0 ? (
            <SopList rows={archivedRows} onSelect={openCard} />
          ) : (
            <div className="rounded-lg border border-dashed border-tcs-border-strong bg-tcs-raised p-8 text-center">
              <p className="text-sm text-tcs-text-secondary">
                No archived accounts match your search/market filter.
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {error && (
            <div className="mt-4">
              <ErrorState body={error} onRetry={refetch} />
            </div>
          )}

          {/* ---------- Filters ---------- */}
          <div className="mt-4">
            <SopFilters
              filters={filters}
              onChange={setFilters}
              availableMarkets={availableMarkets}
              visibleCount={filtered.length}
              totalCount={data.length}
            />
          </div>

          {/* ---------- Drafts strip (#4) — resumable from any device ---------- */}
          {drafts.length > 0 && (
            <div className="mt-4 rounded-lg border border-dashed border-tcs-border-strong bg-st-warn-bg/40 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
                <FileClock className="h-3.5 w-3.5" />
                Drafts ({drafts.length})
              </div>
              <ul className="flex flex-wrap gap-2">
                {drafts.map((d) => (
                  <li key={d.sop_id}>
                    <button
                      type="button"
                      onClick={() => setResumeDraft(d)}
                      className="flex items-center gap-2 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-left text-xs hover:border-tcs-border-strong hover:shadow-sm"
                    >
                      <span className="font-medium text-tcs-text">
                        {cardDisplayName(d)}
                      </span>
                      {d.draft_last_saved_at && (
                        <span className="text-tcs-text-secondary">
                          saved {new Date(d.draft_last_saved_at).toLocaleString()}
                        </span>
                      )}
                      <span className="rounded bg-tcs-surface px-1.5 py-0.5 font-medium text-tcs-text">Resume</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ---------- ⚠ Behind strip (D4) — overdue, unconfirmed starts ---------- */}
          {behindRows.length > 0 && (
            <div className="mt-4 rounded-lg border border-rag-red/40 bg-rag-red/5 p-3">
              <button
                type="button"
                onClick={() => setBehindOpen((v) => !v)}
                className="flex w-full items-center gap-2 text-left"
              >
                <AlertTriangle className="h-4 w-4 shrink-0 text-rag-red" />
                <span className="text-sm font-semibold text-rag-red">
                  {behindRows.length} account{behindRows.length === 1 ? '' : 's'} behind — start date passed, not confirmed started
                </span>
                {behindOpen ? (
                  <ChevronDown className="ml-auto h-4 w-4 text-rag-red" />
                ) : (
                  <ChevronRight className="ml-auto h-4 w-4 text-rag-red" />
                )}
              </button>
              {behindOpen && (
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {behindRows.map((r) => (
                    <li key={r.sop_id}>
                      <button
                        type="button"
                        onClick={() =>
                          setResolution({
                            sopId: r.sop_id,
                            facilityId: r.facility_id ?? null,
                            displayName: cardDisplayName(r),
                            plannedStartDate: r.start_date ?? null,
                          })
                        }
                        className="flex w-full flex-col gap-0.5 rounded-md border border-rag-red/30 bg-tcs-bg px-3 py-2 text-left transition-colors hover:border-rag-red/60 hover:shadow-sm"
                      >
                        <span className="truncate text-sm font-medium text-tcs-text">
                          {cardDisplayName(r)}
                        </span>
                        <span className="text-xs font-medium text-rag-red">{countdownLabel(r) ?? 'No start date'} · resolve →</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ---------- View ---------- */}
          <div className="mt-4">
            {isLoading && liveData.length === 0 ? (
              <div className="rounded-xl border border-tcs-border bg-tcs-bg p-6">
                <Skeleton block height={20} width="40%" />
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Skeleton block height={64} />
                  <Skeleton block height={64} />
                  <Skeleton block height={64} />
                </div>
              </div>
            ) : view === 'kanban' ? (
              <SopKanban
                rows={boardRows}
                onSelect={openCard}
                // Stage-drop optimistic patch. Returns the promise so the
                // Kanban can re-throw on RLS denial and surface inline.
                onStageChange={handleStageDrop}
              />
            ) : (
              <SopList rows={boardRows} onSelect={openCard} />
            )}
          </div>

          {!isLoading && liveData.length === 0 && !error && (
            <div className="mt-6 rounded-lg border border-dashed border-tcs-border-strong bg-tcs-raised p-8 text-center">
              <p className="text-sm text-tcs-text-secondary">
                No SOPs visible. If you expected to see records here, check that
                your role has SELECT access on <code>new_account_sop</code>.
              </p>
            </div>
          )}
        </>
      )}

      {/* ---------- D4 resolution dialog ---------- */}
      {resolution && (
        <ResolutionDialog
          target={resolution}
          onClose={() => setResolution(null)}
          onResolved={refetch}
          onCancelCard={handleCancelCard}
        />
      )}

      {/* ---------- Detail drawer ---------- */}
      {selected && (
        <SopDetail
          row={selected}
          onClose={() => openCard(null)}
          onPatch={handlePatch}
          onDelete={handleDelete}
          onRefetch={refetch}
        />
      )}

      {/* ---------- Create modal (P0 field-unblock) ---------- */}
      <SopCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(sopId, accountName) => {
          toast.success(`Created ${sopId}`, { description: accountName });
          refetch();
        }}
      />

      {/* ---------- Resume-draft modal (#4) ---------- */}
      <SopCreateModal
        open={resumeDraft !== null}
        resumeDraft={resumeDraft}
        onClose={() => setResumeDraft(null)}
        onCreated={(sopId, accountName) => {
          setResumeDraft(null);
          toast.success(`Created ${sopId}`, { description: accountName });
          refetch();
        }}
      />
    </div>
  );
}
